import React, { useEffect } from "react";
import Step1 from "./creator-studio/Step1";
import Step2 from "./creator-studio/Step2";
import AILoader from "../../uiComponents/AILoader";
import { BrandDetails } from "../../../types/add-brand-types";
import useCreatorStudio from "../../../hooks/use-creator-studio";
import Toast from "../../uiComponents/Toast";
import Step3 from "./creator-studio/Step3";
import { ContentFocus, ContentMode } from "../../../types/creator-studio";
import { ContentLog } from "../../../types/dashboard";

interface DraftsProps {
  brandDetails: BrandDetails;
  openAtStep?: number | null;
  onConsumedOpenAtStep?: () => void;
  historyItem?: ContentLog | null;
  onHistoryBack?: () => void;
  prefilledScheduleDate?: Date;
  initialPrompt?: string;
  prefilledPlatforms?: string[];
  weeklyPlanItemId?: string;
}

const Drafts: React.FC<DraftsProps> = ({
  brandDetails,
  openAtStep,
  onConsumedOpenAtStep,
  historyItem,
  onHistoryBack,
  prefilledScheduleDate,
  initialPrompt,
  prefilledPlatforms,
  weeklyPlanItemId,
}) => {
  const {
    step,
    setStep,
    prompt,
    setPrompt,
    additionalInstructions,
    setAdditionalInstructions,
    regenerationFeedback,
    setRegenerationFeedback,
    regenerationFeedbackHistory,
    onRegenerateWithFeedback,
    onClearRegenerationFeedbackHistory,
    selectedTone,
    setSelectedTone,
    selectedTemplateId,
    setSelectedTemplateId,
    creatorMode,
    setCreatorMode,
    contentFocus,
    setContentFocus,
    isLoading,
    handleBack,
    onGenerateCampaign,
    toast,
    closeToast,
    generatedCampaign,
    setSelectedKnowledge,
    onVariationSelect,
    selectedVariation,
    goToOwnPost,
    handleStep3Back,
  } = useCreatorStudio(brandDetails);

  useEffect(() => {
    if (initialPrompt != null) {
      setPrompt(initialPrompt);
    }
  }, [initialPrompt, setPrompt]);

  useEffect(() => {
    if (openAtStep != null) {
      setStep(openAtStep);
      onConsumedOpenAtStep?.();
    }
  }, [openAtStep, setStep, onConsumedOpenAtStep]);

  return (
    <div style={{ position: "relative", flex: 1 }}>
      {isLoading && (
        <AILoader
          type={ContentMode.SOCIAL}
          contextStage={
            contentFocus === ContentFocus.GENERAL_POST
              ? "Reading topic and direction"
              : "Reading brand knowledge"
          }
        />
      )}
      {step === 1 && (
        <Step1
          prompt={prompt}
          setPrompt={setPrompt}
          additionalInstructions={additionalInstructions}
          setAdditionalInstructions={setAdditionalInstructions}
          regenerationFeedbackHistory={regenerationFeedbackHistory}
          onClearRegenerationFeedbackHistory={
            onClearRegenerationFeedbackHistory
          }
          creatorMode={creatorMode}
          setCreatorMode={setCreatorMode}
          contentFocus={contentFocus}
          setContentFocus={setContentFocus}
          selectedTone={selectedTone}
          setSelectedTone={setSelectedTone}
          selectedTemplateId={selectedTemplateId}
          setSelectedTemplateId={setSelectedTemplateId}
          onNext={onGenerateCampaign}
          onSkipToOwnPost={goToOwnPost}
          brandDetails={brandDetails}
          onUserSelectedKnowledge={setSelectedKnowledge}
        />
      )}
      {step === 2 && (
        <Step2
          onBack={handleBack}
          generatedCampaign={generatedCampaign}
          onNext={onVariationSelect}
          initialSelectedVariation={selectedVariation}
          regenerationFeedback={regenerationFeedback}
          setRegenerationFeedback={setRegenerationFeedback}
          regenerationFeedbackHistory={regenerationFeedbackHistory}
          onRegenerateWithFeedback={onRegenerateWithFeedback}
          onClearRegenerationFeedbackHistory={
            onClearRegenerationFeedbackHistory
          }
          isRegenerating={isLoading}
        />
      )}
      {step === 3 && (
        <Step3
          key={historyItem?.id ?? "new"}
          onBack={historyItem ? onHistoryBack ?? handleBack : handleStep3Back}
          selectedVariation={historyItem ? undefined : selectedVariation}
          brandId={brandDetails.id}
          brandDetails={brandDetails}
          historyItem={historyItem ?? undefined}
          onPostSuccess={historyItem ? onHistoryBack ?? handleBack : handleBack}
          prefilledScheduleDate={prefilledScheduleDate}
          prefilledPlatforms={prefilledPlatforms}
          weeklyPlanItemId={weeklyPlanItemId}
          provider="postforme"
        />
      )}
      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
          duration={3000}
        />
      )}
    </div>
  );
};

export default Drafts;
